import React, { useEffect, useState } from "react";

import { useAppSelector } from "../../hooks/reduxHooks";
import { RootState } from "../../store";

// types
import { ManageScoreApiReq } from "../../store/types/configuration";

// common components
import Sidebar from "../../components/common/MagicDrawer_new";

// component
import AddScoreParameterFrom from "../../components/configuration/forms/AddScoreParameterFrom";
import ScoreManagementTabs from "../../components/configuration/ScoreManagementTabs";
import ManageScoreTable from "../../components/configuration/ManageScoreTable";

// Actions
import {
  useParameterTypeQuery,
  useManageScoreQuery,
  useManageScorePostMutation,
  useManageScorePutMutation,
  useEditParameterTypeMutation,
} from "../../store/apis/configurationApis";
import { SubmitHandler, useForm } from "react-hook-form";
import MagicTable from "../../components/common/MagicTable";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Button from "../../components/mui/Button";
import ImageComponent from "../../components/mui/Image";
import Stack from "../../components/mui/Stack";
import { GridCellParams } from "@mui/x-data-grid";
import Box from "../../components/mui/Box";
import { styled } from "@mui/material";
import Pagination from "../../components/mui/Pagination";
import Loader from "../../components/common/Loader";
import { useSelector } from "react-redux";
import { showToast } from "../../helper/toaster";
import usePermission from "../../helper/usePermission";
import EditParameterForm from "./EditParameterForm";
import { acl } from "../../helper/userAccessConstant";

const ManageScoreTableStyled = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: "Arial, sans-serif",
  marginTop: "20px",
  overflow: "hidden",
  borderRadius: "8px",
  marginBottom: "1rem",
  "& th, & td": {
    border: "1px solid #E0E0E0",
    padding: "12px 16px",
    textAlign: "left",
    backgroundColor: "#ffffff",
  },
  "& th": {
    backgroundColor: "#DADADA",
    fontWeight: 600,
    color: "#333",
    margin: "0px",
  },
  "& tbody tr:nth-child(even)": {
    backgroundColor: "#ffffff",
  },
  "& td[rowspan]": {
    backgroundColor: "#ffffff",
    verticalAlign: "middle",
  },
}));

const ManageScore: React.FC = () => {
  const filter = useSelector((state: RootState) => state?.user?.filter);
  const { permissions = {}, isAdmin } = usePermission();

  const { canRead, canWrite, canUpdate, canDelete } =
    permissions[acl.configuration.module]?.manage_score;

  const [isAddParameterForm, setIsAddParameterForm] = useState<boolean>(false);
  const [isEditParameterForm, setIsEditParameterForm] =
    useState<boolean>(false);
  const [manageScoreParams, setManageScoreParams] = useState<ManageScoreApiReq>(
    {}
  );
  const [isEditParameterFormOpen, setIsEditParameterFormOpen] =
    useState<boolean>(false);
  const [isEditParameterTypeForm, setIsEditParameterTypeForm] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<number | null>(0);
  const [params, setParams] = useState({
    parameterTypeId: 1,
    programMasterId: filter?.selectedProgram?.value || undefined,
    quarterMasterId: filter?.selectedQuarter?.quarterMasterId || undefined,
    ProgramYearId: filter?.selectedYear?.value || undefined,
  });

  const [editParameterType, { isLoading: isLoadingEditParameterType }] =
    useEditParameterTypeMutation();

  const { data: parameterType = [], isLoading: isLoadingParameterType } =
    useParameterTypeQuery();
  const {
    data: manageScoreDataApi = [],
    isLoading: isLoadingManageScore,
    refetch: refetchManageScore,
  } = useManageScoreQuery(params);

  const [addScorePost, { isLoading: isLoadingAddScore }] =
    useManageScorePostMutation();
  const [editScorePost, { isLoading: isLoadingEditScore }] =
    useManageScorePutMutation();

  useEffect(() => {
    if (parameterType.length > 0) {
      handleTabChange(parameterType[2]?.parameterTypeId);
    }
  }, [parameterType, isAddParameterForm, isEditParameterForm]);

  useEffect(() => {
    setParams((pre: any) => ({
      ...pre,
      // pageNo: "1",
      // pageSize: "10",
      ...(filter?.selectedProgram?.value && {
        programMasterId: filter?.selectedProgram?.value || undefined,
      }),
      ...(filter?.selectedQuarter?.quarterMasterId && {
        quarterMasterId: filter?.selectedQuarter?.quarterMasterId || undefined,
      }),
      ...(filter?.selectedYear?.value && {
        ProgramYearId: filter?.selectedYear?.value || undefined,
      }),
    }));
  }, [filter]);

  useEffect(() => {
    if (activeTab) {
      refetchManageScore();
    }
  }, [activeTab]);

  const {
    handleSubmit: handleAddParameterFormSubmit,
    control: addParameterFormControl,
    setValue: setParameterFormValue,
    getValues: getParameterFormValues,
    reset: resetAddParameterForm,
    watch: watcher,
  } = useForm({
    defaultValues: {
      parameterFormData: {
        parameterTypeId: undefined,
        parameterName: null,
        parameterWeightage: null,
        programMasterId: filter?.selectedProgram?.value,
        quarterMasterId: filter?.selectedQuarter?.quarterMasterId,
        ProgramYearId: filter?.selectedYear?.value,
        // parameters:[
        //   {
        //     weightageName: null,
        //     measurementCriteria: [
        //       {
        //         measurementCriteriaName: null,
        //         measurementCriteriaPercentage: null,
        //         measurementCriteriaCondition: null,
        //         measurementCriteriaScore: null,
        //         region: null,
        //         subWeightage: null,
        //       },
        //     ],
        //   }
        // ],
        // subParameter: [
        //   {
        //     subParameterName: null,
        //     subParameterWeightage: null,
        //   },
        // ],

        parameters: [
          {
            weightage: "",
            subWeightageParams: [
              {
                subWeightage: "",
                measurementCriteria: [
                  {
                    measurementCriteria: "",
                    score: "",
                    highestScoreCanBeObtained: "",
                    region: "",
                    operator: "",
                    metadata: "",
                    manageScoreId: "",
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  });

  const {
    handleSubmit: handleEditParameterFormSubmit,
    control: editParameterFormControl,
    setValue: setEditParameterFormValue,
    reset: resetEditParameterForm,
    watch: watchEditForm,
  } = useForm({
    defaultValues: {
      parameterFormData: {
        targetParameterId: undefined,
        parameterTypeId: undefined,
        parameterName: null,
        parameterWeightage: null,
        programMasterId: filter?.selectedProgram?.value,
        quarterMasterId: filter?.selectedQuarter?.quarterMasterId,
        ProgramYearId: filter?.selectedYear?.value,
        // parameters:[
        //   {
        //     weightageName: null,
        //     measurementCriteria: [
        //       {
        //         measurementCriteriaName: null,
        //         measurementCriteriaPercentage: null,
        //         measurementCriteriaCondition: null,
        //         measurementCriteriaScore: null,
        //         region: null,
        //         subWeightage: null,
        //       },
        //     ],
        //   }
        // ],
        parameters: [
          {
            weightage: "",
            subWeightageParams: [
              {
                subWeightage: "",
                measurementCriteria: [
                  {
                    measurementCriteria: "",
                    score: "",
                    highestScoreCanBeObtained: "",
                    region: "",
                    operator: "",
                    metadata: "",
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  });

  const {
    handleSubmit: handleEditParameterTypeFormSubmit,
    control: editParameterTypeFormControl,
    setValue: setEditParameterTypeFormValue,
    getValues: getEditParameterTypeFormValues,
    reset: resetEditParameterTypeForm,
    watch: watchParametrType,
  } = useForm({
    defaultValues: {
      parameterFormData: [],
    },
  });

  const toggleAddParameterForm = () => {
    resetAddParameterForm();
    setIsAddParameterForm((pre) => !pre);
  };
  const toggleEditParameterForm = () => {
    setIsEditParameterFormOpen((pre) => !pre);
  };

  const toggleEditParameterTypeForm = () => {
    resetEditParameterTypeForm();
    const preFieldMaped: any = reverseParameterType.map((item: any) => ({
      parameterTypeId: item?.parameterTypeId,
      parametePercentageId: item?.parametePercentageId,
      parameterTypeName: item?.parameterTypeName,
      percentage: item?.percentage || 0,
    }));
    setEditParameterTypeFormValue("parameterFormData", preFieldMaped);

    setIsEditParameterTypeForm((pre: any) => !pre);
  };

  const onPageChange = async (event: any, page: number) => {
    setParams((prevParams) => ({
      ...prevParams,
      pageNo: page.toString(),
      pageSize: "10",
      // ...(searchValue && { search: searchValue }),
    }));
    setCurrentPage(page);
  };

  const handleTabChange = (id: any) => {
    if (!id) {
      return;
    }
    setParams((prevState) => ({
      ...prevState,
      parameterTypeId: id,
    }));

    setParameterFormValue("parameterFormData.parameterTypeId", id);
    // setActiveTab(id);
  };

  let reverseParameterType = parameterType
    .map((item: any) => ({
      ...item,
      parameterTypeId: item?.parameterTypeId,
      parameterTypeName:
        item?.parameterTypeName === "Programme performance"
          ? "Programme Performance Parameters"
          : " Quality and Efficacy Parameters",
      percentage: item?.percentage,
    }))
    .slice();

  const handleAddParameterBtn = () => {
    setIsAddParameterForm((pre) => !pre);
  };

  const toggleEditParameter = () => {
    setIsEditParameterFormOpen((pre) => !pre);
  };

  const handleEditParameter = () => {};

  const handleEditBtnClick = (row: any, weightIndex: number) => {
    resetEditParameterForm();
    const operatorOptions = [
      { id: "<", label: "Less Than" },
      { id: "<=", label: "Less Than or Equal To" },
      { id: ">", label: "Greater Than" },
      { id: ">=", label: "Greater Than or Equal To" },
      { id: "==", label: "Equal To" },
      { id: "!=", label: "Not Equal To" },
      { id: "&&", label: "And" },
      { id: "||", label: "Or" },
    ];

    const findOperator = (operator: string) => {
      const foundOperator = operatorOptions.find(
        (item: any) => item.id === operator
      );
      return { label: foundOperator?.label, id: foundOperator?.id };
    };

    // const selectedWeightage = row?.targetParameters[weightIndex]?.weightages[0] || null;

    const selectedWeightage = row?.parameter?.weightages?.[weightIndex] || null;

    // Format the parameters for the selected weightage
    const setFormatedParametrs = {
      weightageId: selectedWeightage?.weightageId,
      weightage: selectedWeightage?.weightage,
      subWeightageParams: Array.isArray(selectedWeightage?.subWeightages)
        ? selectedWeightage?.subWeightages?.map((criteria: any) => ({
            subWeightage: criteria?.subWeightage || 0,
            measurementCriteria: criteria?.measurementCriteria?.map(
              (item: any) => ({
                measurementCriteriaId: item?.measurementCriteriaId,
                measurementCriteria: item?.measurementCriteria,
                score: item?.score || 0,
                highestScoreCanBeObtained: item?.highestScoreCanBeObtained || 0,
                region: item?.region,
                operator: findOperator(item?.operator),
                subDescription: row?.parameterName,
                metadata:
                  item?.metadata === null
                    ? "{}"
                    : JSON.stringify(item?.metadata),
                manageScoreId: item?.manageScoreId,
              })
            ),
          })) || [
            {
              subWeightage: "",
              measurementCriteria: [
                {
                  measurementCriteria: "",
                  score: "",
                  highestScoreCanBeObtained: "",
                  region: "",
                  operator: "",
                  metadata: "",
                  manageScoreId: "",
                },
              ],
            },
          ]
        : [
            {
              subWeightage: "",
              measurementCriteria: [
                {
                  measurementCriteria: "",
                  score: "",
                  highestScoreCanBeObtained: "",
                  region: "",
                  operator: "",
                  metadata: "",
                  manageScoreId: "",
                },
              ],
            },
          ],
    };

    setEditParameterFormValue("parameterFormData.parameterTypeId", row.typeId);
    setEditParameterFormValue(
      "parameterFormData.parameterName",
      row?.parameter?.parameterName
    );
    setEditParameterFormValue(
      "parameterFormData.targetParameterId",
      row?.parameter?.targetParameterId
    );
    // setEditParameterFormValue(
    //   "parameterFormData.manageScoreId",
    //   row.manageScoreId
    // );
    setEditParameterFormValue(
      "parameterFormData.programMasterId",
      row.programId
    );
    setEditParameterFormValue(
      "parameterFormData.quarterMasterId",
      row.quarterId
    );
    setEditParameterFormValue("parameterFormData.ProgramYearId", row.yearId);
    setEditParameterFormValue("parameterFormData.parameters", [
      setFormatedParametrs,
    ]);
    toggleEditParameterForm();
  };

  const calculateRowSpan = (weightages: any[]) => {
    return weightages.reduce(
      (sum, weight) => sum + weight.subWeightageParameter.length,
      0
    );
  };

  const handleEditParameterTypeSubmit = async (data: any) => {
    try {
      const payload = data?.parameterFormData?.map((i: any, idx: number) => ({
        parameterTypeId: i?.parameterTypeId,
        parametePercentageId: i?.parametePercentageId,
        newPercentage: parseInt(i?.percentage),
      }));

      const res = await editParameterType(payload);
      if (res.data?.success) {
        showToast(
          res?.data?.message || "Parameter Type updated successfully",
          "success"
        );
        setIsEditParameterTypeForm((pre: any) => !pre);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddParameterFromDoneBtn = handleAddParameterFormSubmit(
    async (data: any) => {
      const payload = {
        parameterTypeId: data?.parameterFormData?.parameterTypeId,
        parameterName: data.parameterFormData.parameterName,
        programMasterId: filter?.selectedProgram?.value,
        quarterMasterId: filter?.selectedQuarter?.quarterMasterId,
        ProgramYearId: filter?.selectedYear?.value,
        parameters: data.parameterFormData.parameters.map((param: any) => ({
          weightage: param.weightage,
          MeasurementCriteria: param.measurementCriteria.map(
            (criteria: any) => ({
              ...(criteria?.measurementCriteriaId && {
                measurementCriteriaId: criteria?.measurementCriteriaId,
              }),
              measurementCriteria: criteria?.measurementCriteria,
              score: criteria?.score,
              highestScoreCanBeObtained: criteria?.highestScoreCanBeObtained,
              region: criteria?.region,
              operator: criteria?.operator?.id,
              metadata: JSON.stringify(criteria?.metadata),
            })
          ),
        })),
      };

      try {
        const res = await addScorePost(payload);
        if (res?.data.success) {
          resetAddParameterForm();
          toggleAddParameterForm();
        }
      } catch (error) {
        console.log(error);
      }
    }
  );

  const handleEditParameterFromDoneBtn = handleEditParameterFormSubmit(
    async (data: any) => {
      const safeJsonParse = (input: string, defaultValue: any = {}) => {
        try {
          return JSON.parse(input);
        } catch (error) {
          showToast(
            "Invalid JSON format in metadata. Please check the input.",
            "error"
          );
          return defaultValue;
        }
      };

      const payload = {
        targetParameterId: data?.parameterFormData.targetParameterId,
        programMasterId: filter?.selectedProgram?.value || undefined,
        quarterMasterId: filter?.selectedQuarter?.quarterMasterId || undefined,
        ProgramYearId: filter?.selectedYear?.value || undefined,
        parameters: data?.parameterFormData?.parameters?.map((param: any) => ({
          weightage: param?.weightage || 0,
          subWeightageParams: param?.subWeightageParams?.map(
            (subWeightage: any) => ({
              subWeightage: subWeightage?.subWeightage || 0,
              measurementCriteria: subWeightage?.measurementCriteria?.map(
                (criteria: any) => {
                  return {
                    ...(criteria?.measurementCriteriaId && {
                      measurementCriteriaId: criteria?.measurementCriteriaId,
                    }),
                    measurementCriteria: criteria?.measurementCriteria,
                    score: criteria?.score || 0,
                    highestScoreCanBeObtained:
                      criteria?.highestScoreCanBeObtained || 0,
                    region: criteria?.region,
                    operator: criteria?.operator?.id,
                    ...(criteria?.metadata !== "{}" && {
                      metadata: safeJsonParse(criteria?.metadata),
                    }),
                    ...(criteria?.manageScoreId !== null && { manageScoreId: criteria?.manageScoreId }),
                  };
                }
              ),
            })
          ),
        })),
      };

      const manageScoreId = data?.parameterFormData?.manageScoreId;

      try {
        const res = await editScorePost({ payload });
        if (res?.data.success) {
          showToast("Parameter Edited successfully", "success");
          toggleEditParameterForm();
          resetEditParameterForm();
        }
      } catch (error) {
        console.log(error);
      }
    }
  );

  const handleAddParameterFromCancelBtn = () => {
    toggleAddParameterForm();
    resetAddParameterForm();
  };
  const handleEditParameterFromCancelBtn = () => {
    toggleEditParameterForm();
    resetEditParameterForm();
  };

  const handleEditParameterBtn = () => {};

  // const tableRowCount = manageScoreDataApi?.totalRowCount || 0;

  // const totalPages = Math.ceil(tableRowCount / 10);

  return (
    <div>
      <ScoreManagementTabs
        scoreTabs={reverseParameterType}
        isLoadingScoreTabs={isLoadingParameterType}
        handleTabChange={handleTabChange}
        handleAddParameterBtn={handleAddParameterBtn}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleEditParameterBtn={toggleEditParameterTypeForm}
        canWrite={canWrite}
      />

      {activeTab === 0 && (
        <>
          <Box sx={{ width: "100%" }}>
            {isLoadingManageScore ? (
              <Loader loading={isLoadingManageScore} />
            ) : (
              <ManageScoreTableStyled>
                <thead>
                  <tr>
                    <th style={{ borderRadius: "8px 0 0 8px" }}>Parameter</th>
                    <th>Parameter/Sub parameter description</th>
                    <th>Weightage</th>
                    <th>
                      Sub-parameter
                      <br /> Weightage
                    </th>
                    <th>Measurement Criteria</th>
                    <th>Meta Data</th>
                    <th>Score</th>
                    <th style={{ borderRadius: "0 8px 8px 0" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(manageScoreDataApi) &&
                  manageScoreDataApi?.length > 0 ? (
                    manageScoreDataApi.map(
                      (category: any, categoryIndex: any) => {
                        const { categories, targetParameters } = category;

                        const targetRows = targetParameters.flatMap(
                          (parameter: any) =>
                            parameter.weightages.flatMap((weightage: any) =>
                              weightage?.subWeightages.flatMap(
                                (subWeightage: any) =>
                                  subWeightage?.measurementCriteria.map(
                                    (measurementCriteriaItem: any) => ({
                                      parameter,
                                      weightage,
                                      subWeightage,
                                      measurementCriteriaItem,
                                    })
                                  )
                              )
                            )
                        );

                        return targetRows.map((row: any, rowIndex: any) => {
                          const isFirstCategoryRow = rowIndex === 0;

                          const isFirstParameterRow =
                            targetRows.findIndex(
                              (item: any) => item.parameter === row.parameter
                            ) === rowIndex;

                          const isFirstWeightageRow =
                            targetRows.findIndex(
                              (item: any) =>
                                item.parameter === row.parameter &&
                                item.weightage === row.weightage
                            ) === rowIndex;

                          const isFirstSubWeightageRow =
                            targetRows.findIndex(
                              (item: any) =>
                                item.parameter === row.parameter &&
                                item.weightage === row.weightage &&
                                item.subWeightage === row.subWeightage
                            ) === rowIndex;

                          return (
                            <tr key={`row-${categoryIndex}-${rowIndex}`}>
                              {/* Render Category */}
                              {isFirstCategoryRow && (
                                <td
                                  rowSpan={targetRows.length}
                                  style={{ width: "250px" }}
                                >
                                  {categories}
                                </td>
                              )}

                              {/* Render Parameter */}
                              {isFirstParameterRow && (
                                <td
                                  rowSpan={
                                    targetRows.filter(
                                      (item: any) =>
                                        item.parameter === row.parameter
                                    ).length
                                  }
                                >
                                  {row.parameter.parameterName}
                                </td>
                              )}

                              {/* Render Weightage */}
                              {isFirstWeightageRow && (
                                <td
                                  rowSpan={
                                    targetRows.filter(
                                      (item: any) =>
                                        item.parameter === row.parameter &&
                                        item.weightage === row.weightage
                                    ).length
                                  }
                                >
                                  {row.weightage?.weightage || "0"}
                                </td>
                              )}

                              {/* Render Sub-parameter Weightage */}
                              {isFirstSubWeightageRow && (
                                <td
                                  rowSpan={
                                    targetRows.filter(
                                      (item: any) =>
                                        item.parameter === row.parameter &&
                                        item.weightage === row.weightage &&
                                        item.subWeightage === row.subWeightage
                                    ).length
                                  }
                                >
                                  {Number(row.subWeightage?.subWeightage) > 0
                                    ? row.subWeightage?.subWeightage || "0"
                                    : 0}
                                </td>
                              )}

                              {/* Measurement Criteria */}
                              <td>
                                {row.measurementCriteriaItem
                                  ?.measurementCriteria || "-"}
                              </td>

                              {/* Meta Data */}
                              <td>
                                {row.measurementCriteriaItem?.metadata
                                  ? Object.entries(
                                      row.measurementCriteriaItem.metadata
                                    ).map(([key, value]: [string, any]) =>
                                      ["column"].includes(key) ? null : (
                                        <div key={key}>
                                          <strong>{key}:</strong> {value}
                                        </div>
                                      )
                                    )
                                  : "-"}
                              </td>

                              {/* Score */}
                              <td>
                                {row.measurementCriteriaItem?.score || "0"}
                              </td>

                              {/* Action */}
                              {isFirstWeightageRow && (
                                <td
                                  rowSpan={
                                    targetRows.filter(
                                      (item: any) =>
                                        item.parameter === row.parameter &&
                                        item.weightage === row.weightage
                                    ).length
                                  }
                                >
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      border: "1px solid #EAECF0",
                                      padding: "9px 0px !important",
                                      borderRadius: "12px",
                                      minWidth: "45px",
                                    }}
                                    disabled={!canUpdate}
                                    onClick={() => {
                                      const weightIndex =
                                        row.parameter.weightages.findIndex(
                                          (weightage: any) =>
                                            weightage === row.weightage
                                        );
                                      handleEditBtnClick(row, weightIndex);
                                    }}
                                  >
                                    <ImageComponent
                                      src="/assets/images/icon/editIcon.svg"
                                      alt="editPen"
                                    />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          );
                        });
                      }
                    )
                  ) : (
                    <tr>
                      <td colSpan={8} style={{ textAlign: "center" }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </ManageScoreTableStyled>
            )}
          </Box>
        </>
      )}

      {activeTab === 1 && (
        <>
          <Box sx={{ width: "100%" }}>
            {isLoadingManageScore ? (
              <Loader loading={isLoadingManageScore} />
            ) : (
              <ManageScoreTableStyled>
                <thead>
                  <tr>
                    <th style={{ borderRadius: "8px 0 0 8px" }}>Parameter</th>
                    <th>Parameter/Sub parameter description</th>
                    <th>Weightage</th>
                    <th>
                      Sub-parameter
                      <br /> Weightage
                    </th>
                    <th>Measurement Criteria</th>
                    <th>Meta Data</th>
                    <th>Score</th>
                    <th style={{ borderRadius: "0 8px 8px 0" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(manageScoreDataApi) &&
                  manageScoreDataApi?.length > 0 ? (
                    manageScoreDataApi.map(
                      (category: any, categoryIndex: any) => {
                        const { categories, targetParameters } = category;

                        const targetRows = targetParameters.flatMap(
                          (parameter: any) =>
                            parameter.weightages.flatMap(
                              (weightage: any, weightageIndex: number) =>
                                weightage?.subWeightages.flatMap(
                                  (subWeightage: any) =>
                                    subWeightage?.measurementCriteria.map(
                                      (measurementCriteriaItem: any) => ({
                                        parameter,
                                        weightage,
                                        subWeightage,
                                        measurementCriteriaItem,
                                      })
                                    )
                                )
                            )
                        );

                        return targetRows.map((row: any, rowIndex: any) => {
                          const isFirstCategoryRow = rowIndex === 0;

                          const isFirstParameterRow =
                            targetRows.findIndex(
                              (item: any) => item.parameter === row.parameter
                            ) === rowIndex;

                          const isFirstWeightageRow =
                            targetRows.findIndex(
                              (item: any) =>
                                item.parameter === row.parameter &&
                                item.weightage === row.weightage
                            ) === rowIndex;

                          const isFirstSubWeightageRow =
                            targetRows.findIndex(
                              (item: any) =>
                                item.parameter === row.parameter &&
                                item.weightage === row.weightage &&
                                item.subWeightage === row.subWeightage
                            ) === rowIndex;

                          return (
                            <tr key={`row-${categoryIndex}-${rowIndex}`}>
                              {/* Render Category */}
                              {isFirstCategoryRow && (
                                <td
                                  rowSpan={targetRows.length}
                                  style={{ width: "250px" }}
                                >
                                  {categories}
                                </td>
                              )}

                              {/* Render Parameter */}
                              {isFirstParameterRow && (
                                <td
                                  rowSpan={
                                    targetRows.filter(
                                      (item: any) =>
                                        item.parameter === row.parameter
                                    ).length
                                  }
                                >
                                  {row.parameter.parameterName}
                                </td>
                              )}

                              {/* Render Weightage */}
                              {isFirstWeightageRow && (
                                <td
                                  rowSpan={
                                    targetRows.filter(
                                      (item: any) =>
                                        item.parameter === row.parameter &&
                                        item.weightage === row.weightage
                                    ).length
                                  }
                                >
                                  {row.weightage?.weightage || "0"}
                                </td>
                              )}

                              {/* Render Sub-parameter Weightage */}
                              {isFirstSubWeightageRow && (
                                <td
                                  rowSpan={
                                    targetRows.filter(
                                      (item: any) =>
                                        item.parameter === row.parameter &&
                                        item.weightage === row.weightage &&
                                        item.subWeightage === row.subWeightage
                                    ).length
                                  }
                                >
                                  {Number(row.subWeightage?.subWeightage) >= 0
                                    ? row.subWeightage?.subWeightage || "0"
                                    : 0}
                                </td>
                              )}

                              {/* Measurement Criteria */}
                              <td>
                                {row.measurementCriteriaItem
                                  ?.measurementCriteria || "-"}
                              </td>

                              {/* Meta Data */}
                              <td>
                                {row.measurementCriteriaItem?.metadata
                                  ? Object.entries(
                                      row.measurementCriteriaItem.metadata
                                    ).map(([key, value]: [string, any]) =>
                                      ["column"].includes(key) ? null : (
                                        <div key={key}>
                                          <strong>{key}:</strong> {value}
                                        </div>
                                      )
                                    )
                                  : "-"}
                              </td>

                              {/* Score */}
                              <td>
                                {row.measurementCriteriaItem?.score || "0"}
                              </td>

                              {/* Action */}
                              {isFirstWeightageRow && (
                                <td
                                  rowSpan={
                                    targetRows.filter(
                                      (item: any) =>
                                        item.parameter === row.parameter &&
                                        item.weightage === row.weightage
                                    ).length
                                  }
                                >
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      border: "1px solid #EAECF0",
                                      padding: "9px 0px !important",
                                      borderRadius: "12px",
                                      minWidth: "45px",
                                    }}
                                    disabled={!canUpdate}
                                    onClick={() => {
                                      const weightIndex =
                                        row.parameter.weightages.findIndex(
                                          (weightage: any) =>
                                            weightage === row.weightage
                                        );
                                      handleEditBtnClick(row, weightIndex);
                                    }}
                                  >
                                    <ImageComponent
                                      src="/assets/images/icon/editIcon.svg"
                                      alt="editPen"
                                    />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          );
                        });
                      }
                    )
                  ) : (
                    <tr>
                      <td colSpan={8} style={{ textAlign: "center" }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </ManageScoreTableStyled>
            )}
          </Box>
        </>
      )}

      <Sidebar
        title="Add Parameter"
        subtitle="Here you can add parameters by filling the details."
        drawerOpen={isAddParameterForm}
        onDrawerToggle={toggleAddParameterForm}
        // width="470px"
        sx={{ width: "470px" }}
        actionComponent={() => (
          <>
            <Button
              className="bun-outline"
              onClick={handleAddParameterFromCancelBtn}
              // loading={isLoadingEditScore}
              disabled={isLoadingAddScore}
            >
              Cancel
            </Button>
            <Button
              className="bun-filled"
              onClick={handleAddParameterFromDoneBtn}
              loading={isLoadingAddScore}
              disabled={isLoadingAddScore}
            >
              Done
            </Button>
          </>
        )}
      >
        <AddScoreParameterFrom control={addParameterFormControl} />
      </Sidebar>

      <Sidebar
        title="Edit Parameter"
        subtitle="Here you can edit parameters score."
        drawerOpen={isEditParameterFormOpen}
        onDrawerToggle={toggleEditParameterForm}
        // width="470px"
        sx={{ width: "520px" }}
        actionComponent={() => (
          <>
            <Button
              className="bun-outline"
              onClick={handleEditParameterFromCancelBtn}
              // loading={isLoadingEditScore}
              disabled={isLoadingEditScore}
            >
              Cancel
            </Button>
            <Button
              className="bun-filled"
              onClick={handleEditParameterFromDoneBtn}
              loading={isLoadingEditScore}
              disabled={isLoadingEditScore}
            >
              Done
            </Button>
          </>
        )}
      >
        <AddScoreParameterFrom
          control={editParameterFormControl}
          row={watchEditForm()}
        />
      </Sidebar>

      <Sidebar
        title="Edit Parameter %"
        subtitle="Here you can edit parameters percentage."
        drawerOpen={isEditParameterTypeForm}
        onDrawerToggle={toggleEditParameterTypeForm}
        // width="470px"
        actionComponent={() => (
          <>
            <Button
              className="bun-outline"
              onClick={toggleEditParameterTypeForm}
              loading={isLoadingEditScore}
              disabled={isLoadingEditScore}
            >
              Cancel
            </Button>
            <Button
              className="bun-filled"
              onClick={handleEditParameterTypeFormSubmit(
                handleEditParameterTypeSubmit
              )}
              loading={isLoadingEditScore}
              disabled={isLoadingEditScore}
            >
              Done
            </Button>
          </>
        )}
      >
        <EditParameterForm
          control={editParameterTypeFormControl}
          watch={watchParametrType}
        />
      </Sidebar>
    </div>
  );
};

export default ManageScore;
